import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { MONTHS_FR } from '../utils/dateUtils';
import { getEntityColors } from '../utils/geoUtils';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  Clock,
  Users,
  Building2,
  Edit2,
  Archive
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

// --- CONSTANTES ---
type FilterMode = 'week' | 'month' | 'year';

// Utilitaires pour les semaines ISO
const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getWeekStartDate = (year: number, week: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
};

const getWeekEndDate = (weekStart: Date): Date => {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
};

// --- TYPES ---
interface DiversProject {
  id: string;
  code: string;
  name: string;
  parent_entity: string | null;
  zone?: string | number | null;
  address?: string | null;
  is_active: boolean;
}

interface DiversSubProject {
  id: string;
  parent_project_id: string;
  divers_number: string;
  name: string;
  address?: string | null;
  zone?: string | number | null;
  is_active: boolean;
  created_at: string;
}

interface TimeEntryWithDetails {
  id: string;
  work_date: string;
  hours_normal: number;
  project_id: string;
  employee_name: string;
  description?: string | null;
}

interface SubProjectWithHours extends DiversSubProject {
  totalHours: number;
  entries: TimeEntryWithDetails[];
  employeeCount: number;
}

// EntityData est utilisé dans les commentaires pour documenter la structure
// mais n'est pas directement instancié dans le code actuel

export default function AdminDivers() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<DiversProject[]>([]);
  const [subProjects, setSubProjects] = useState<DiversSubProject[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntryWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubProjects, setExpandedSubProjects] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // --- ÉTATS DU FILTRE DE DATE ---
  const [filterMode, setFilterMode] = useState<FilterMode>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(getISOWeekNumber(new Date()));

  const toggleSubProjectExpanded = (subProjectId: string) => {
    setExpandedSubProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subProjectId)) newSet.delete(subProjectId);
      else newSet.add(subProjectId);
      return newSet;
    });
  };

  // --- Calcul des dates de début/fin selon le mode ---
  const { startDate, endDate, periodLabel } = useMemo(() => {
    let start: Date, end: Date, label: string;

    if (filterMode === 'week') {
      start = getWeekStartDate(selectedYear, selectedWeek);
      end = getWeekEndDate(start);
      label = `Semaine ${selectedWeek} (${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })})`;
    } else if (filterMode === 'month') {
      start = new Date(selectedYear, selectedMonth, 1);
      end = new Date(selectedYear, selectedMonth + 1, 0);
      label = `${MONTHS_FR[selectedMonth]} ${selectedYear}`;
    } else {
      start = new Date(selectedYear, 0, 1);
      end = new Date(selectedYear, 11, 31);
      label = `Année ${selectedYear}`;
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      periodLabel: label
    };
  }, [filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- Navigation temporelle ---
  const handlePrevious = () => {
    if (filterMode === 'week') {
      if (selectedWeek > 1) setSelectedWeek(selectedWeek - 1);
      else { setSelectedYear(selectedYear - 1); setSelectedWeek(52); }
    } else if (filterMode === 'month') {
      if (selectedMonth > 0) setSelectedMonth(selectedMonth - 1);
      else { setSelectedYear(selectedYear - 1); setSelectedMonth(11); }
    } else {
      setSelectedYear(selectedYear - 1);
    }
  };

  const handleNext = () => {
    if (filterMode === 'week') {
      if (selectedWeek < 52) setSelectedWeek(selectedWeek + 1);
      else { setSelectedYear(selectedYear + 1); setSelectedWeek(1); }
    } else if (filterMode === 'month') {
      if (selectedMonth < 11) setSelectedMonth(selectedMonth + 1);
      else { setSelectedYear(selectedYear + 1); setSelectedMonth(0); }
    } else {
      setSelectedYear(selectedYear + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
    setSelectedWeek(getISOWeekNumber(now));
  };

  // --- CHARGEMENT DES DONNÉES ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Charger les projets Divers parents (category = 'divers', exclure SYS-FORM)
      const { data: projectsData, error: projError } = await supabase
        .from('projects')
        .select('id, code, name, parent_entity, zone, address, is_active')
        .eq('category', 'divers')
        .eq('is_active', true)
        .neq('code', 'SYS-FORM')
        .order('parent_entity', { ascending: true });

      if (projError) throw projError;

      // 2. Charger les sous-chantiers Divers
      const { data: subProjectsData, error: subError } = await supabase
        .from('divers_sub_projects')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (!subError && subProjectsData) {
        setSubProjects(subProjectsData as DiversSubProject[]);
      }

      // 3. Charger les time_entries pour les projets parents sur la période
      const projectIds = (projectsData || []).map(p => p.id);

      if (projectIds.length > 0) {
        const { data: entriesData, error: entriesError } = await supabase
          .from('time_entries')
          .select(`
            id, work_date, hours_normal, project_id, description,
            weekly_sheets!inner (
              employee:employees!weekly_sheets_employee_id_fkey ( full_name )
            )
          `)
          .in('project_id', projectIds)
          .gte('work_date', startDate)
          .lte('work_date', endDate);

        if (entriesError) throw entriesError;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mappedEntries: TimeEntryWithDetails[] = (entriesData || []).map((e: any) => ({
          id: e.id,
          work_date: e.work_date,
          hours_normal: e.hours_normal,
          project_id: e.project_id,
          description: e.description,
          employee_name: e.weekly_sheets?.employee?.full_name || 'Inconnu'
        }));

        setTimeEntries(mappedEntries);
      } else {
        setTimeEntries([]);
      }

      setProjects((projectsData || []) as DiversProject[]);

    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- REGROUPEMENT DYNAMIQUE PAR GROUPE DIVERS ---
  interface GroupData {
    project: DiversProject;
    subProjects: SubProjectWithHours[];
    totalHours: number;
    unassignedEntries: TimeEntryWithDetails[];
  }

  const { groupsData, totalHours } = useMemo(() => {
    // Fonction pour enrichir les sous-projets avec les heures
    const enrichSubProjects = (parentId: string): SubProjectWithHours[] => {
      const parentSubProjects = subProjects.filter(sp => sp.parent_project_id === parentId);
      const parentEntries = timeEntries.filter(e => e.project_id === parentId);

      return parentSubProjects.map(sp => {
        // Associer les entrées au sous-projet via la description (contient le nom du sous-chantier)
        const subEntries = parentEntries.filter(e =>
          e.description && e.description.toLowerCase().includes(sp.name.toLowerCase())
        );
        const uniqueEmployees = new Set(subEntries.map(e => e.employee_name));

        return {
          ...sp,
          totalHours: subEntries.reduce((acc, e) => acc + e.hours_normal, 0),
          entries: subEntries,
          employeeCount: uniqueEmployees.size
        };
      });
    };

    // Calculer les heures pour les entrées sans sous-chantier associé
    const getUnassignedEntries = (parentId: string, subProjectsList: SubProjectWithHours[]): TimeEntryWithDetails[] => {
      const parentEntries = timeEntries.filter(e => e.project_id === parentId);
      const assignedIds = new Set(subProjectsList.flatMap(sp => sp.entries.map(e => e.id)));
      return parentEntries.filter(e => !assignedIds.has(e.id));
    };

    // Construire les données pour chaque groupe Divers
    const data: GroupData[] = projects.map(project => {
      const projectSubProjects = enrichSubProjects(project.id);
      const unassigned = getUnassignedEntries(project.id, projectSubProjects);
      const hours = projectSubProjects.reduce((acc, sp) => acc + sp.totalHours, 0) +
        unassigned.reduce((acc, e) => acc + e.hours_normal, 0);

      return {
        project,
        subProjects: projectSubProjects,
        totalHours: hours,
        unassignedEntries: unassigned
      };
    });

    // Trier par entité parente (JLO en premier) puis par nom
    data.sort((a, b) => {
      if (a.project.parent_entity === 'JLO' && b.project.parent_entity !== 'JLO') return -1;
      if (a.project.parent_entity !== 'JLO' && b.project.parent_entity === 'JLO') return 1;
      return a.project.name.localeCompare(b.project.name);
    });

    const total = data.reduce((acc, g) => acc + g.totalHours, 0);

    return { groupsData: data, totalHours: total };
  }, [projects, subProjects, timeEntries]);

  // Données pour le graphique (regroupées par entité parente)
  const chartData = useMemo(() => {
    const jloHours = groupsData
      .filter(g => g.project.parent_entity === 'JLO')
      .reduce((acc, g) => acc + g.totalHours, 0);
    const gpHours = groupsData
      .filter(g => g.project.parent_entity === 'GP')
      .reduce((acc, g) => acc + g.totalHours, 0);
    const wagHours = groupsData
      .filter(g => g.project.parent_entity === 'WAG')
      .reduce((acc, g) => acc + g.totalHours, 0);

    return {
      labels: ['JLO Divers', 'GP Divers', 'WAG Divers'],
      datasets: [{
        data: [jloHours, gpHours, wagHours],
        backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
        borderWidth: 0
      }]
    };
  }, [groupsData]);

  // --- RENDU D'UNE SECTION DE GROUPE DIVERS ---
  const renderGroupSection = (groupData: GroupData) => {
    const { project, subProjects, totalHours, unassignedEntries } = groupData;
    const ec = getEntityColors(project.parent_entity);
    const color = ec.text;
    const bgColor = ec.bg;
    const borderColor = ec.border;

    const isExpanded = expandedGroups.has(project.id);
    const toggleExpanded = () => {
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        if (newSet.has(project.id)) newSet.delete(project.id);
        else newSet.add(project.id);
        return newSet;
      });
    };

    return (
      <div
        key={project.id}
        style={{
          background: 'white',
          borderRadius: '16px',
          border: `1px solid ${borderColor}`,
          overflow: 'hidden',
          marginBottom: '20px'
        }}
      >
        {/* Header du groupe */}
        <div
          onClick={toggleExpanded}
          style={{
            padding: '20px 24px',
            background: bgColor,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isExpanded ? `1px solid ${borderColor}` : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {isExpanded ? <ChevronUp size={20} color={color} /> : <ChevronDown size={20} color={color} />}
            <div>
              <div style={{ fontWeight: '700', fontSize: '1.2rem', color: color }}>{project.name}</div>
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{project.parent_entity} • Code: {project.code}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Sous-chantiers</div>
              <div style={{ fontWeight: '800', fontSize: '1.5rem', color: color }}>{subProjects.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Heures Période</div>
              <div style={{ fontWeight: '800', fontSize: '1.5rem', color: color }}>{totalHours}h</div>
            </div>
          </div>
        </div>

        {/* Liste des sous-chantiers */}
        {isExpanded && (
          <div style={{ padding: '0' }}>
            {subProjects.length === 0 && unassignedEntries.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                Aucun sous-chantier créé pour {project.name}
              </div>
            ) : (
              <>
                {subProjects.map((subProject, idx) => {
                  const isSubExpanded = expandedSubProjects.has(subProject.id);

                  return (
                    <div
                      key={subProject.id}
                      style={{
                        borderBottom: idx < subProjects.length - 1 ? '1px solid #f1f5f9' : 'none'
                      }}
                    >
                      {/* Ligne du sous-chantier */}
                      <div
                        onClick={() => toggleSubProjectExpanded(subProject.id)}
                        style={{
                          padding: '16px 24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          background: isSubExpanded ? '#fafbfc' : 'white',
                          transition: 'background 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {isSubExpanded ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
                          <span style={{
                            background: '#f1f5f9',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                            fontWeight: '700',
                            fontSize: '0.85rem',
                            color: '#475569'
                          }}>
                            {subProject.divers_number}
                          </span>
                          <div>
                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem' }}>
                              {subProject.name}
                            </div>
                            {subProject.zone && (
                              <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                <MapPin size={12} /> Zone {subProject.zone}
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                          {subProject.employeeCount > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '0.85rem' }}>
                              <Users size={14} /> {subProject.employeeCount}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={16} color={color} />
                            <span style={{ fontWeight: '700', fontSize: '1.1rem', color: color }}>
                              {subProject.totalHours}h
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Détail des entrées */}
                      {isSubExpanded && subProject.entries.length > 0 && (
                        <div style={{ background: '#f8fafc', padding: '12px 24px 16px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '8px 0', textAlign: 'left', color: '#64748b', fontWeight: '600' }}>Date</th>
                                <th style={{ padding: '8px 0', textAlign: 'left', color: '#64748b', fontWeight: '600' }}>Employé</th>
                                <th style={{ padding: '8px 0', textAlign: 'right', color: '#64748b', fontWeight: '600' }}>Heures</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subProject.entries.map(entry => (
                                <tr key={entry.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '10px 0', color: '#475569' }}>
                                    {new Date(entry.work_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </td>
                                  <td style={{ padding: '10px 0', color: '#1e293b', fontWeight: '500' }}>
                                    {entry.employee_name}
                                  </td>
                                  <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: '700', color }}>
                                    {entry.hours_normal}h
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {isSubExpanded && subProject.entries.length === 0 && (
                        <div style={{ padding: '20px 24px', background: '#f8fafc', color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>
                          Aucune heure saisie sur cette période
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Heures non assignées à un sous-chantier */}
                {unassignedEntries.length > 0 && (
                  <div style={{ borderTop: '1px dashed #e2e8f0', padding: '16px 24px', background: '#fffbeb' }}>
                    <div style={{ fontWeight: '600', color: '#b45309', marginBottom: '12px', fontSize: '0.9rem' }}>
                      Heures non rattachées ({unassignedEntries.reduce((acc, e) => acc + e.hours_normal, 0)}h)
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <tbody>
                        {unassignedEntries.map(entry => (
                          <tr key={entry.id} style={{ borderBottom: '1px solid #fef3c7' }}>
                            <td style={{ padding: '8px 0', color: '#92400e' }}>
                              {new Date(entry.work_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </td>
                            <td style={{ padding: '8px 0', color: '#78350f', fontWeight: '500' }}>
                              {entry.employee_name}
                            </td>
                            <td style={{ padding: '8px 0', color: '#64748b', fontStyle: 'italic' }}>
                              {entry.description || 'Pas de description'}
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '700', color: '#b45309' }}>
                              {entry.hours_normal}h
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}>
          <ArrowLeft size={20} color="#64748b" />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Gestion des Affaires Diverses</h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#64748b' }}>Vue consolidée par entité (JLO / GP)</p>
        </div>
      </div>

      {/* STATS + FILTRES */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '30px' }}>

        {/* STATS */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
          <div style={{ width: '120px', height: '120px' }}>
            {totalHours > 0 ? (
              <Doughnut data={chartData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
            ) : (
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', border: '4px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '0.7rem' }}>0h</div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Total Période : <strong style={{ color: '#1e293b', fontSize: '1.1rem' }}>{totalHours} h</strong></div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.85rem' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }}></div> JLO Divers : <strong>{groupsData.filter(g => g.project.parent_entity === 'JLO').reduce((acc, g) => acc + g.totalHours, 0)} h</strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.85rem' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }}></div> GP Divers : <strong>{groupsData.filter(g => g.project.parent_entity === 'GP').reduce((acc, g) => acc + g.totalHours, 0)} h</strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.85rem' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }}></div> WAG Divers : <strong>{groupsData.filter(g => g.project.parent_entity === 'WAG').reduce((acc, g) => acc + g.totalHours, 0)} h</strong>
            </div>
          </div>
        </div>

        {/* FILTRE */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '15px' }}>

          {/* Sélecteur de mode */}
          <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
            {(['week', 'month', 'year'] as FilterMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setFilterMode(mode)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  background: filterMode === mode ? '#2563eb' : 'transparent',
                  color: filterMode === mode ? 'white' : '#64748b',
                  transition: 'all 0.2s'
                }}
              >
                {mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Année'}
              </button>
            ))}
          </div>

          {/* Navigation + Label période */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={handlePrevious} style={{ padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', color: '#64748b' }}>
              <ChevronLeft size={18} />
            </button>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Calendar size={16} color="#2563eb" />
                <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '1rem' }}>{periodLabel}</span>
              </div>
            </div>
            <button onClick={handleNext} style={{ padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', color: '#64748b' }}>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Sélecteurs détaillés selon le mode */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{ flex: 1, minWidth: '100px', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', cursor: 'pointer' }}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            {(filterMode === 'month' || filterMode === 'week') && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={{ flex: 2, minWidth: '120px', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                {MONTHS_FR.map((m, idx) => (
                  <option key={idx} value={idx}>{m}</option>
                ))}
              </select>
            )}

            {filterMode === 'week' && (
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                style={{ flex: 1, minWidth: '100px', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                  <option key={w} value={w}>S{w}</option>
                ))}
              </select>
            )}
          </div>

          <button
            onClick={handleToday}
            style={{
              padding: '10px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              color: '#15803d',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            Aujourd'hui
          </button>
        </div>
      </div>

      {/* LIEN ADMIN PROJETS */}
      <div style={{
        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Building2 size={20} color="#64748b" />
          <span style={{ color: '#475569', fontSize: '0.9rem' }}>
            Pour créer ou modifier des chantiers Divers, utilisez la page <strong>Projets & Affaires</strong>
          </span>
        </div>
        <button
          onClick={() => navigate('/admin/projects')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'white',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            color: '#475569',
            fontSize: '0.85rem'
          }}
        >
          <Edit2 size={16} /> Gérer les projets
        </button>
      </div>

      {/* CONTENU PRINCIPAL */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>Chargement des données...</div>
      ) : (
        <>
          {/* Sections des groupes Divers */}
          {groupsData.map(groupData => renderGroupSection(groupData))}

          {/* Message si aucun projet */}
          {groupsData.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
              background: 'white',
              borderRadius: '16px',
              border: '1px dashed #cbd5e1',
              color: '#64748b'
            }}>
              <Archive size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
              <p style={{ fontSize: '1.1rem', fontWeight: '500', margin: 0 }}>Aucun chantier Divers actif</p>
              <p style={{ fontSize: '0.9rem', marginTop: '8px', color: '#94a3b8' }}>
                Créez des chantiers Divers depuis le Planificateur ou la page Projets
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
